<template>
  <div>
    <div v-if="isI18n">
      <div class="card-item mt-6">
        <CollectionButtons :clients="clients" @filters="filters.isModalVisible = true" />
        <b-field class="m-5">
          <FindEntitiesInput
            placeholder="Buscar cliente"
            :expanded="true"
            class="search-clients"
            :finder="finder"
            @select="clickedOnClient($event, checkUserFeature('element.collection.button.gestionar') &&
              (!$event.defaultingCollectionUserId))" />
        </b-field>
        <div>
          <h5 class="has-text-centered">
            {{ $t('filterText') }} <span class="has-text-weight-bold">{{ numberOfDocuments }}
              cliente{{ clients.length > 1 ? 's' : '' }}</span>
            {{ $t('withLate') }} <span class="has-text-weight-bold">
              {{
                filteredClients.length > 0
                  ? `${$t('showing')} ${filteredClients.length}`
                  : $t('withOutResult')
              }}
            </span>
          </h5>
        </div>
        <div class="container">
          <Spinner v-if="isLoading" />
          <Message v-if="clients.length===0 && !isLoading" :message="mainMessage.message" :icon="mainMessage.icon" :as-card="false" />
          <div v-if="!isLoading" id="clients-list" data-cy="clients-list" class="default-items">
            <div
              v-for="client in filteredClients"
              :id="`client-${client._id}`"
              :key="client._id"
              class="card-item shadowless default-item-info has-text-centered"
              :class="[
                `${client.totalNumberOfDefaultingPayments}-defaulting-payments`,
                !client.defaultingCollectionUserId ? '' :
                currentUserId === client.defaultingCollectionUserId ?
                  'managed-by-current-user' : 'managed-by-another-user',
                client.numberOfDefaultingDays >= 90 ? 'ninety-days' :
                client.numberOfDefaultingDays >= 60 ? 'sixty-days' :
                client.numberOfDefaultingDays >= 30 ? 'thirty-days' : 'healthy-days'
              ]">
              <div class="default-item-numbers">
                <b-tooltip
                  :label="$t('lateDays')"
                  data-cy="default-item-days"
                  class="default-item-days">
                  <i class="fa fa-calendar-alt content-title med-size-text" />
                  <span class="big-size-text">{{ client.numberOfDefaultingDays }}</span>
                </b-tooltip>
                <b-tooltip :label="$t('lateOperations')" data-cy="default-item-operations" class="default-item-operations">
                  <i class="fa fa-home content-title med-size-text" />
                  <span class="big-size-text">{{ client.defaultingOperations.length }}</span>
                </b-tooltip>
                <b-tooltip :label="$t('lateFees')" data-cy="default-item-fees" class="default-item-fees">
                  <i class="fa fa-layer-group content-title med-size-text" />
                  <span class="big-size-text">
                    {{ client.totalNumberOfDefaultingPayments }}
                  </span>
                </b-tooltip>
              </div>
              <div class="default-item-client med-size-text">
                <div class="content-title">{{ client.name }} {{ client.lastName }}</div>
                <div>{{ formatIdNumber(client.idNumber) }}</div>
              </div>
              <div data-cy="default-item-manage" class="default-item-manage med-size-text">
                <button
                  v-if="checkUserFeature('element.collection.button.gestionar') &&
                    (!client.defaultingCollectionUserId || currentUserId === client.defaultingCollectionUserId)"
                  :disabled="!checkUserFeature('element.collection.button.gestionar')"
                  class="button manage-button is-button-secondary is-rounded"
                  data-cy="manage-button"
                  @click="clickedOnClient(client, !client.defaultingCollectionUserId)">
                  <span> {{ $t('manage') }} </span>
                </button>
                <button
                  v-else
                  :disabled="!checkUserFeature('element.collection.button.ver-cliente')"
                  class="button manage-button is-button-secondary is-rounded"
                  data-cy="manage-button"
                  @click="clickedOnClient(client, false)">
                  <span> {{ $t('justSee') }} </span>
                </button>
              </div>
            </div>
          </div>
        </div>
        <creditu-pagination
          v-if="!isLoading"
          :page-number.sync="pageNumber"
          size="is-small"
          :documents-per-page.sync="documentsPerPage"
          items-name="Clientes"
          :total="numberOfDocuments"
          :range-before="1"
          :range-after="1"
          :documents-per-page-options="[5, 10, 20, 50, 10000]" />
      </div>
      <CollectionFilters v-model="filters" />
    </div>
  </div>
</template>

<script>
import {
  getDefaultingClients,
  findClientsBySearchString,
  setOrUnsetClientDefaultAsBeingManagedByCurrentUser
} from '@/application/services/api-clients/clients';
import Message from '@/ui/components/Message.vue';
import Spinner from '@/ui/components/Spinner.vue';
import CollectionFilters from '@/ui/components/internal/collection/CollectionFilters.vue';
import FindEntitiesInput from '@/ui/components/internal/FindEntitiesInput.vue';
import CollectionButtons from '@/ui/components/internal/collection/CollectionButtons.vue';
import CredituPagination from '@/ui/components/CredituPagination.vue';
import { formatNumber } from '@/infrastructure/format';
import userFeatures from '@/ui/mixins/user-features';
import notification from '@/ui/mixins/notification';

export default {
  name: 'Collection',
  components: { Message, Spinner, CollectionFilters, FindEntitiesInput, CollectionButtons, CredituPagination },
  mixins: [notification],
  data() {
    const emptyFilters = {
      numberOfDefaultingPaymentsOptions: [0, 1, 2, 3, 4, 5, 6, 7],
      isModalVisible: false,
      useMaxDefaultingOperation: false,
      defaultingPayments: 0,
      defaultingDays: [],
      typeOperation: '',
      operationSubType: '',
      operationOrigin: '',
      operationInvestor: ''
    };
    const { currentUser, filters: newFilters } = this.$store.state;
    const filters = newFilters || emptyFilters;
    const date = new Date();
    return {
      date,
      pageNumber: 1,
      numberOfDocuments: 0,
      documentsPerPage: 10,
      mainMessage: {},
      isLoading: false,
      clients: [],
      filteredClients: [],
      filters,
      currentUserId: currentUser.user._id,
      cancelGetDefaultingClients: null,
      downloadingClientsDefaults: false,
      finder: findClientsBySearchString,
      errorMessage: {}
    };
  },
  computed: {
    isI18n() { return ['br', 'cl', 'pe'].includes(this.$i18n.locale); }
  },
  watch: {
    async documentsPerPage() {
      this.pageNumber = 1;
      if (this.isI18n) await this.getDefaultingClients();
    },
    pageNumber: {
      immediate: true,
      async handler() {
        if (this.isI18n) await this.getDefaultingClients();
      }
    },
    filters: {
      deep: true,
      async handler({ isModalVisible }) {
        if (isModalVisible) {
          return;
        }
        if (this.isI18n) await this.getDefaultingClients();
      }
    }
  },
  methods: {
    // Por algún motivo el mixins no funciona en este modulo.
    checkUserFeature(name) {
      return userFeatures.methods.checkUserFeature(name, this.$store);
    },
    async getDefaultingClients() {
      if (this.isLoading) {
        this.cancelGetDefaultingClients('cancelled');
      }
      this.isLoading = true;
      try {
        const dueDate = this.date.toISOString().substring(0, 10);
        const cancelExecutor = cancelFunction => { this.cancelGetDefaultingClients = cancelFunction; };
        const [minDefaultingDays, maxDefaultingDays] = this.filters.defaultingDays;

        const { clients, pagination } = await getDefaultingClients(
          dueDate,
          maxDefaultingDays,
          minDefaultingDays,
          this.filters.defaultingPayments > 0 ? this.filters.defaultingPayments : undefined,
          this.filters.useMaxDefaultingOperation,
          this.documentsPerPage,
          this.pageNumber,
          this.filters.typeOperation,
          this.filters.operationSubType,
          this.filters.operationOrigin,
          this.filters.operationInvestor,
          false,
          cancelExecutor
        );
        this.clients = clients;
        this.pageNumber = pagination.pageNumber;
        this.numberOfDocuments = pagination.numberOfDocuments;
        this.filteredClients = this.clients;
        if (this.clients.length === 0) {
          this.mainMessage = { message: 'No hay clientes morosos', icon: 'sin-cuotas-pendientes.svg' };
        }
      } catch (error) {
        if (error.message === 'cancelled') {
          return;
        }
        this.mainMessage = {
          message: 'Ocurrió un error al tratar de obtener los clientes morosos',
          icon: 'data_maintenance.svg'
        };
      }
      this.isLoading = false;
    },
    formatIdNumber(idNumber) {
      if (this.$i18n.locale === 'pe') {
        return idNumber;
      }
      const splittedIdNumber = idNumber.split('-');
      const number = Number(splittedIdNumber[0]);
      return `${formatNumber({ number })}-${splittedIdNumber[1]}`;
    },
    async clickedOnClient(client, manage) {
      const clientId = client._id;
      if (manage) {
        this.isLoading = true;
        try {
          await setOrUnsetClientDefaultAsBeingManagedByCurrentUser(clientId);
        } catch (_error) {
          this.alert({ message: 'Ocurrió un error al asignar usuario activo al cliente' });
          return;
        }
      }
      this.$router.push({ name: 'client-debt', params: { clientId } });
    }
  }
};
</script>

<style scoped>
.card-item {
  overflow: inherit;
  margin-bottom: 10rem;
}
.search-clients {
  max-width: 350px;
  margin: auto;
}
.button { margin-inline: 1%; }
.big-size-text {font-size: 1.2rem;}
.med-size-text {font-size: 0.9rem;}
.card-item.default-item-info {
  margin: 2rem 0.5rem;
  padding: 1rem 2rem;
}
.default-items .default-item-info {
  width: auto;
  display: grid;
  grid:
    "numbers name button" auto
    / 3.5fr 3fr auto;
  gap: 0rem 1rem;
  place-items: center;
  place-content: center stretch;
}
.default-item-info .default-item-numbers {
  width: 100%;
  display: grid;
  grid:
    "days operations fees" auto
    / 1.5fr 1fr 1fr;
  gap: 0rem 1rem;
  place-items: center;
  place-content: center stretch;
}
.default-item-info > .default-item-numbers > div,
.default-item-info > div:not(:first-child),
i {
  display: flex;
  flex-flow: row wrap;
  align-items: center;
  align-content: center;
  justify-content: center;
}
.default-item-info .default-item-numbers > div > :first-child,
.default-item-info .default-item-client > :first-child {
  margin: 0.5rem;
}
.default-item-info .default-item-days {
  font-weight: 600;
}
.card-item.managed-by-current-user {
  border: 1px solid var(--primary);
}
.card-item.managed-by-another-user {
  border: 1px solid var(--yellow);
}
.ninety-days .default-item-days {
  color: var(--secondary-dark);
}
.sixty-days .default-item-days {
  color: var(--secondary);
}
.thirty-days .default-item-days {
  color: var(--yellow);
}
.healthy-days .default-item-days {
  color: var(--primary);
}
.content-title {
  color: var(--primary);
  font-weight: 600;
}
@media (max-width: 800px) {
  .big-size-text {font-size: 0.9rem;}
  .med-size-text {font-size: 0.75rem;}
}
@media (max-width: 570px) {
  .big-size-text {font-size: 1.2rem;}
  .med-size-text {font-size: 0.9rem;}
  .default-items .default-item-info {
    grid:
      "numbers" auto
      "name" auto
      "button" auto
      / auto;
    place-content: center stretch;
  }
  .default-item-info .default-item-numbers {
    width: auto;
    display: flex;
    display: flex;
    flex-flow: row wrap;
    align-items: center;
    align-content: center;
    justify-content: center;
  }
  .default-item-info .default-item-numbers > div:not(:first-child) {
    margin: 0rem 0rem 0rem 0.5rem;
  }
  @media (max-width: 350px) {
    .big-size-text {font-size: 0.9rem;}
    .med-size-text {font-size: 0.75rem;}
    .card-item.default-item-info {padding: 1rem 1rem;}
  }
}
</style>

<i18n>
{
  "cl": {
    "latestClients": "Clientes morosos",
    "filterText": "Para este filtro hay ",
    "withLate": "con mora, ",
    "showing": "mostrando",
    "withOutResult":"ninguno coincide con el filtro",
    "manage": "Gestionar",
    "justSee": "Ver",
    "lateDays": "Días de mora",
    "lateOperations": "Operaciones con mora",
    "lateFees":"Cuotas en mora"
  },
  "pe": {
    "latestClients": "Clientes morosos",
    "filterText": "Para este filtro hay ",
    "withLate": "con mora, ",
    "showing": "mostrando",
    "withOutResult":"ninguno coincide con el filtro",
    "manage": "Gestionar",
    "justSee": "Ver",
    "lateDays": "Días de mora",
    "lateOperations": "Operaciones con mora",
    "lateFees":"Cuotas en mora"
  },
  "br": {
    "latestClients": "Clientes com parcelas em aberto",
    "filterText": "Para este filtro tem ",
    "withLate": "com parcelas em aberto, ",
    "showing": "mostrando",
    "withOutResult":"ninguno coincide com o filtro",
    "manage": "Atuar",
    "justSee": "Ver",
    "lateDays": "Dias em atraso",
    "lateOperations": "Operações vencidas",
    "lateFees":"Parcelas em atraso"
  }
}
</i18n>
